import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { buildVendorMonthsForYear, vendorTripMonthKey } from '../../utils/vendorPayoutStatus';
import '../admin/BookingCalendar.scss';

/**
 * Month-click grid for vendor order pages (same UX as Booking Calendar).
 */
export default function VendorOrdersCalendar({
  items = [],
  getMonthKey = vendorTripMonthKey,
  loading,
  emptyMessage = 'No orders yet.',
  renderTable,
  payoutAudience = 'vendor',
}) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const rows = useMemo(
    () => items.map((item) => ({ item, monthKey: getMonthKey(item) })),
    [items, getMonthKey]
  );

  const months = useMemo(
    () => buildVendorMonthsForYear(rows, year, { audience: payoutAudience }),
    [rows, year, payoutAudience]
  );

  const selectedYearMonth = selectedMonth ? `${year}-${String(selectedMonth).padStart(2, '0')}` : null;
  const monthItems = useMemo(() => {
    if (!selectedYearMonth) return [];
    return rows.filter((r) => r.monthKey === selectedYearMonth).map((r) => r.item);
  }, [rows, selectedYearMonth]);

  const monthLabel = selectedMonth
    ? moment(`${year}-${String(selectedMonth).padStart(2, '0')}-01`).format('MMMM YYYY')
    : null;

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm text-primary" role="status"><span className="sr-only">Loading…</span></div>
      </div>
    );
  }

  const hasItems = items.length > 0;

  return (
    <div className="booking-cal">
      {!hasItems && emptyMessage && (
        <p className="text-muted small mb-3 mb-md-0">{emptyMessage}</p>
      )}
      <div className="d-flex justify-content-end mb-2">
        <div className="booking-cal__year-nav">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setYear((y) => y - 1); setSelectedMonth(null); }}>‹</button>
          <span className="booking-cal__year-label">{year}</span>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setYear((y) => y + 1); setSelectedMonth(null); }}>›</button>
        </div>
      </div>

      <div className="booking-cal__grid mb-3" role="list">
        {months.map((m) => {
          const isSelected = selectedMonth === m.month;
          const isCurrentMonth = year === currentYear && m.month === new Date().getMonth() + 1;
          const detailParts = [];
          if (m.paid > 0) detailParts.push(`${m.paid} paid`);
          if (m.unpaid > 0) detailParts.push(`${m.unpaid} awaiting pay`);
          if (m.notCredited > 0) detailParts.push(`${m.notCredited} in progress`);
          return (
            <button
              key={m.yearMonth}
              type="button"
              role="listitem"
              className={`booking-cal__cell${isSelected ? ' booking-cal__cell--selected' : ''}${isCurrentMonth ? ' booking-cal__cell--current' : ''}`}
              onClick={() => setSelectedMonth((prev) => (prev === m.month ? null : m.month))}
              aria-pressed={isSelected}
            >
              <span className="booking-cal__cell-label">{m.label}</span>
              <span className="booking-cal__month-line-total d-block text-center">{m.total}</span>
              {m.total > 0 && detailParts.length > 0 && (
                <span className="booking-cal__month-line-detail d-block text-center">
                  {detailParts.join(' · ')}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {!selectedMonth && (
        <p className="text-muted text-center small">
          {hasItems ? 'Click a month to see your orders.' : 'Click a month — trips will appear here once admin assigns you.'}
        </p>
      )}

      {selectedMonth && (
        <section className="booking-cal__detail">
          <h3 className="h6 mb-3">{monthLabel} ({monthItems.length})</h3>
          {monthItems.length === 0 ? (
            <p className="text-muted mb-0">No orders this month.</p>
          ) : (
            renderTable(monthItems)
          )}
        </section>
      )}
    </div>
  );
}
